import {ConsentHistory, UserConsent} from '@/types'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HistoryIcon from '@mui/icons-material/History'
import PersonIcon from '@mui/icons-material/Person'
import PolicyIcon from '@mui/icons-material/Policy'
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from '@mui/material'
import * as React from 'react'
import {useEffect, useState} from 'react'
import {
	DateField,
	FunctionField,
	Loading,
	Show,
	Tab,
	TabbedShowLayout,
	TextField,
	useDataProvider,
	useShowController
} from 'react-admin'
import {useNavigate} from 'react-router-dom'

export default function UserConsentShow() {
	const {record, isLoading} = useShowController<UserConsent>()
	const [history, setHistory] = useState<ConsentHistory[]>([])
	const [historyLoading, setHistoryLoading] = useState(true)
	const [historyError, setHistoryError] = useState<string | null>(null)
	const dataProvider = useDataProvider()
	const navigate = useNavigate()

	useEffect(() => {
		if (record?.id) {
			fetchHistory()
		}
	}, [record?.id])

	const fetchHistory = async () => {
		if (!record?.id) return

		try {
			setHistoryLoading(true)
			// Utiliser une approche directe pour récupérer l'historique
			const url = `${process.env.REACT_APP_DB_LOPT}/user-consents/${record.id}/history?limit=100`
			const response = await fetch(url, {
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`,
					'Accept': 'application/json'
				}
			})

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`)
			}

			const data = await response.json()
			setHistory(Array.isArray(data) ? data : [])
			setHistoryError(null)
		} catch (error: any) {
			setHistoryError(error?.message || 'Erreur lors du chargement de l\'historique')
			console.error('Error fetching history:', error)
		} finally {
			setHistoryLoading(false)
		}
	}

	if (isLoading) return <Loading/>

	const handleBackToPurpose = () => {
		if (record?.consentPurpose?.id) {
			navigate(`/consent/purposes/${record.consentPurpose.id}/show`)
		}
	}

	return (
		<Show title={<Title/>}>
			<Stack p={3} pb={2} gap={2} alignItems={'flex-start'}>
				<Typography variant={'h5'}>Usage : {record.consentPurpose.title}</Typography>
				{record?.consentPurpose && (
					<Button
						variant="outlined"
						startIcon={<ArrowBackIcon/>}
						onClick={handleBackToPurpose}
						size="small"
						sx={{
							whiteSpace: 'nowrap',
							'&:hover': {
								backgroundColor: 'primary.main',
								color: 'white',
								borderColor: 'primary.main'
							}
						}}
					>
						Retour à l'usage RGPD
					</Button>
				)}
			</Stack>
			<TabbedShowLayout>
				<Tab label="Informations générales" icon={<PersonIcon/>}>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<Card>
								<CardContent>
									<Typography variant="h6" gutterBottom color="primary">
										<PersonIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
										Utilisateur
									</Typography>
									<Stack spacing={2}>
										<Box>
											<Typography variant="body2" color="textSecondary">
												Email
											</Typography>
											<TextField source="user.email"/>
										</Box>
										<Box>
											<Typography variant="body2" color="textSecondary">
												Nom complet
											</Typography>
											<Typography variant="body1">
												{record?.user?.firstName} {record?.user?.lastName}
											</Typography>
										</Box>
										<Box>
											<Typography variant="body2" color="textSecondary">
												Token (email hashé)
											</Typography>
											<TextField source="hashed_email"
													   sx={{fontFamily: 'monospace', fontSize: '0.9em'}}/>
										</Box>
									</Stack>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12} md={6}>
							<Card sx={{
								position: 'relative',
								'&:hover': {
									boxShadow: 4,
									cursor: 'pointer'
								}
							}}>
								<CardContent>
									<Typography variant="h6" gutterBottom color="primary">
										<PolicyIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
										Usage RGPD
									</Typography>
									<Stack spacing={2}>
										<Box>
											<Typography variant="body2" color="textSecondary">
												Titre
											</Typography>
											<TextField source="consentPurpose.title"/>
										</Box>
										<Box>
											<Typography variant="body2" color="textSecondary">
												Code
											</Typography>
											<TextField source="consentPurpose.code"/>
										</Box>
										<Box>
											<Typography variant="body2" color="textSecondary">
												Version
											</Typography>
											<Chip
												size="small"
												label={record?.consentPurpose?.version}
												color={record?.consentPurpose?.versionType === 'MAJOR' ? 'error' : 'default'}
											/>
										</Box>
										<Box sx={{mt: 2}}>
											<Button
												variant="text"
												startIcon={<PolicyIcon/>}
												onClick={() => {
													if (record?.consentPurpose?.id) {
														navigate(`/consent/purposes/${record.consentPurpose.id}/show`)
													}
												}}
												size="small"
												color="primary"
											>
												Voir la fiche complète
											</Button>
										</Box>
									</Stack>
								</CardContent>
							</Card>
						</Grid>
					</Grid>

					<Card sx={{mt: 3}}>
						<CardContent>
							<Typography variant="h6" gutterBottom>
								Statut du consentement
							</Typography>
							<Grid container spacing={3}>
								<Grid item xs={12} md={4}>
									<FunctionField
										source="isGranted"
										label="Statut actuel"
										render={(record: UserConsent) => (
											<Box sx={{textAlign: 'center', p: 2}}>
												<Chip
													label={record.isGranted ? 'ACCORDÉ' : 'RÉVOQUÉ'}
													color={record.isGranted ? 'success' : 'error'}
													icon={record.isGranted ? <CheckCircleIcon/> : <CancelIcon/>}
													size="medium"
													sx={{fontSize: '1rem', px: 2, py: 1}}
												/>
											</Box>
										)}
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<Box>
										<Typography variant="body2" color="textSecondary">
											Date du consentement
										</Typography>
										<DateField source="consentedAt" showTime/>
									</Box>
								</Grid>
								<Grid item xs={12} md={4}>
									{record?.revokedAt && (
										<Box>
											<Typography variant="body2" color="textSecondary">
												Date de révocation
											</Typography>
											<DateField source="revokedAt" showTime/>
										</Box>
									)}
								</Grid>
							</Grid>
						</CardContent>
					</Card>

					<Card sx={{mt: 3}}>
						<CardContent>
							<Typography variant="h6" gutterBottom>
								Informations techniques
							</Typography>
							<Grid container spacing={3}>
								<Grid item xs={12} md={4}>
									<Box>
										<Typography variant="body2" color="textSecondary">
											User Agent
										</Typography>
										<Typography variant="body2"
													sx={{fontFamily: 'monospace', wordBreak: 'break-word'}}>
											{record?.userAgent || 'Non disponible'}
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={12} md={4}>
									<Box>
										<Typography variant="body2" color="textSecondary">
											IP (hashé)
										</Typography>
										<Typography variant="body2" sx={{fontFamily: 'monospace'}}>
											{record?.ipHash || 'Non disponible'}
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={12} md={4}>
									<Box>
										<Typography variant="body2" color="textSecondary">
											Créé le
										</Typography>
										<DateField source="createdAt" showTime/>
									</Box>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Tab>

				<Tab label="Historique" icon={<HistoryIcon/>}>
					<Typography variant="h6" gutterBottom>
						<HistoryIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
						Historique des modifications
					</Typography>

					{historyLoading ? (
						<Loading/>
					) : historyError ? (
						<Alert severity="error">
							{historyError}
						</Alert>
					) : history.length === 0 ? (
						<Alert severity="info">
							Aucun historique disponible pour ce consentement.
						</Alert>
					) : (
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Action</TableCell>
										<TableCell>Valeur précédente</TableCell>
										<TableCell>Nouvelle valeur</TableCell>
										<TableCell>Date</TableCell>
										<TableCell>User Agent</TableCell>
										<TableCell>IP</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{history.map((entry) => (
										<TableRow key={entry.id}>
											<TableCell>
												<Chip
													label={entry.actionLabel || entry.action}
													color={entry.action === 'GRANTED' ? 'success' : 'error'}
													size="small"
													icon={entry.action === 'GRANTED' ? <CheckCircleIcon/> :
														<CancelIcon/>}
												/>
											</TableCell>
											<TableCell>
												<Typography variant="caption" sx={{fontFamily: 'monospace'}}>
													{entry.previousValue ? JSON.stringify(entry.previousValue, null, 2) : '-'}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="caption" sx={{fontFamily: 'monospace'}}>
													{entry.newValue ? JSON.stringify(entry.newValue, null, 2) : '-'}
												</Typography>
											</TableCell>
											<TableCell>
												{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '-'}
											</TableCell>
											<TableCell>
												<Typography variant="caption"
															sx={{maxWidth: 200, wordBreak: 'break-word'}}>
													{entry.userAgent || '-'}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="caption" sx={{fontFamily: 'monospace'}}>
													{entry.ipHash || '-'}
												</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Tab>
			</TabbedShowLayout>
		</Show>
	)
}

const Title = () => {
	const {record} = useShowController<UserConsent>()
	// const navigate = useNavigate()
	//
	// const handleBackToPurpose = () => {
	// 	if (record?.consentPurpose?.id) {
	// 		navigate(`/consent/purposes/${record.consentPurpose.id}/show`)
	// 	}
	// }

	return (
		<Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1}}>
				<Typography variant="h5">
					Consentement
				</Typography>
				{record?.user && (
					<Typography variant="h6" color="textSecondary">
						- {record.user.firstName} {record.user.lastName}
					</Typography>
				)}
				{record?.consentPurpose && (
					<Chip
						label={record.consentPurpose.title}
						size="small"
						color="primary"
						variant="outlined"
					/>
				)}
			</Box>

			{/*{record?.consentPurpose && (*/}
			{/*	<Button*/}
			{/*		variant="outlined"*/}
			{/*		startIcon={<ArrowBackIcon/>}*/}
			{/*		onClick={handleBackToPurpose}*/}
			{/*		size="small"*/}
			{/*		sx={{*/}
			{/*			whiteSpace: 'nowrap',*/}
			{/*			'&:hover': {*/}
			{/*				backgroundColor: 'primary.main',*/}
			{/*				color: 'white',*/}
			{/*				borderColor: 'primary.main'*/}
			{/*			}*/}
			{/*		}}*/}
			{/*	>*/}
			{/*		Retour à l'usage RGPD*/}
			{/*	</Button>*/}
			{/*)}*/}
		</Box>
	)
}
